import { compact } from "lodash";
import moment from "moment";
import { Dispatch, SetStateAction, useCallback, useMemo } from "react";
import colors from "tailwindcss/colors";
import {
  AssessmentGroupAnonymity,
  AssessmentGroupProviders,
  AssessmentTemplateComplianceRequirement,
  AssessmentType,
  ComplianceProgramCalibrationType,
  ComplianceProgramRecurrence,
  Maybe,
  TopicTemplateComplianceRequirement,
} from "types/graphql-schema";

import useLabel from "@apps/use-label/use-label";
import ComboboxGeneric, {
  ComboboxGenericOption,
} from "@components/combobox/generic-combobox";
import FormFields from "@components/form/form-fields";
import HeroButtons from "@components/hero-buttons/hero-buttons";
import Input from "@components/input/input";
import InputDatePicker from "@components/input/input-datepicker";
import DeprecatedSelect, { Select } from "@components/select/select";
import {
  ToggleButtonGroup,
  ToggleButtonGroupTheme,
} from "@components/toggle-button-group/toggle-button-group";
import ToggleSwitch from "@components/toggle-switch/toggle-switch";
import InfoTooltip from "@components/tooltip/info-tooltip";
import TopicflowIcon, {
  getTopicflowIcon,
} from "@components/topicflow-icon/topicflow-icon";
import {
  assessmentGroupAnonymityOptions,
  assessmentGroupProviderOptions,
} from "@helpers/constants";
import { classNames } from "@helpers/css";
import { assertNonNull } from "@helpers/helpers";

import { ComplianceProgramStep } from "../compliance-program-form-steps";
import {
  AssessmentTemplateExtraSettings,
  ComplianceProgramType,
  DateRangeType,
  ProgramEvaluationPeriodType,
  ProgramForm,
  ProgramStartTrigger,
} from "../types";

const Step = ({
  enabled,
  title,
  settings,
  disabled,
  infoTooltip,
  toggleTooltip,
  onChange,
}: {
  enabled: boolean;
  title: string;
  settings?: React.ReactNode;
  disabled?: boolean;
  infoTooltip?: string;
  toggleTooltip?: string;
  onChange: (enabled: boolean) => void;
}) => {
  return (
    <div
      className={classNames(
        "border border-2 rounded-lg px-4 py-4 flex justify-between",
        enabled && "border-blue-100 bg-sky-50",
        !enabled && "border-dashed border-gray-200 bg-white"
      )}
    >
      <div className="shrink-0 flex gap-3">
        <div className="shrink-0">
          <ToggleSwitch
            disabled={disabled}
            tooltip={toggleTooltip}
            checked={enabled}
            onChange={onChange}
            activeColor={colors.blue[600]}
            aria-label={title}
          />
        </div>
        <div className="flex-1">
          <div className="tf-header-sm">{title}</div>
          <div className="mt-3 empty:hidden">{settings}</div>
        </div>
      </div>
      {infoTooltip && <InfoTooltip text={infoTooltip} />}
    </div>
  );
};

const ProgramFormWorkflow = ({
  complianceProgramType,
  complianceProgramSteps,
  canUpdateTemplates,
  proposedComplianceProgram,
  canUpdate,
  selectedProgramPeriodRange,
  availableTopicTemplates,
  onUpdateAssessmentTemplateSettings,
  onUpdateComplianceProgramSteps,
  onUpdateProposedComplianceProgram,
  setSelectedProgramPeriodRange,
  onChangeComplianceProgramType,
}: {
  complianceProgramType: ComplianceProgramType;
  complianceProgramSteps: ComplianceProgramStep[];
  canUpdateTemplates: boolean;
  proposedComplianceProgram: ProgramForm;
  canUpdate: boolean;
  selectedProgramPeriodRange: DateRangeType;
  availableTopicTemplates: ComboboxGenericOption<number>[];
  onUpdateAssessmentTemplateSettings: (
    assessmentType: AssessmentType
  ) => (settings: AssessmentTemplateExtraSettings) => void;
  onUpdateComplianceProgramSteps: (steps: ComplianceProgramStep[]) => void;
  onUpdateProposedComplianceProgram: Dispatch<SetStateAction<ProgramForm>>;
  setSelectedProgramPeriodRange: (range: DateRangeType) => void;
  onChangeComplianceProgramType: (newType: ComplianceProgramType) => void;
}) => {
  const label = useLabel();

  const selectedAssessmentTemplates = useMemo(
    () =>
      proposedComplianceProgram.assessmentTemplates
        ? compact(
            proposedComplianceProgram.assessmentTemplates as Maybe<AssessmentTemplateComplianceRequirement>[]
          )
        : [],
    [proposedComplianceProgram.assessmentTemplates]
  );

  const selectedTopicTemplateId = useMemo(
    () =>
      proposedComplianceProgram.topicTemplates
        ? (proposedComplianceProgram.topicTemplates as Maybe<TopicTemplateComplianceRequirement>[])
        : [],
    [proposedComplianceProgram.topicTemplates]
  );

  const selectedManagerAssessmentTemplate = useMemo(() => {
    return selectedAssessmentTemplates.find(
      (template) => template.assessmentType === AssessmentType.Manager
    );
  }, [selectedAssessmentTemplates]);
  const selectedPeerAssessmentTemplate = useMemo(() => {
    return selectedAssessmentTemplates.find(
      (template) => template.assessmentType === AssessmentType.Peer
    );
  }, [selectedAssessmentTemplates]);
  const selectedEngagementSurveyAssessmentTemplate = useMemo(() => {
    return selectedAssessmentTemplates.find(
      (template) => template.assessmentType === AssessmentType.EngagementSurvey
    );
  }, [selectedAssessmentTemplates]);

  const handleToggleStep = useCallback(
    (step: ComplianceProgramStep) => {
      if (complianceProgramSteps.includes(step)) {
        onUpdateComplianceProgramSteps(
          complianceProgramSteps.filter((s) => s !== step)
        );
        if (
          step === ComplianceProgramStep.SELF_MANAGER_REVIEW &&
          proposedComplianceProgram.calibration !==
            ComplianceProgramCalibrationType.Off
        ) {
          onUpdateProposedComplianceProgram((prev) => ({
            ...prev,
            calibration: ComplianceProgramCalibrationType.Off,
          }));
        }
      } else {
        onUpdateComplianceProgramSteps([...complianceProgramSteps, step]);
      }
    },
    [
      complianceProgramSteps,
      onUpdateComplianceProgramSteps,
      onUpdateProposedComplianceProgram,
      proposedComplianceProgram,
    ]
  );

  return (
    <>
      <div className="mt-8 mb-4 text-gray-600 text-sm">
        What type of program will this be?
      </div>
      <HeroButtons
        buttons={[
          {
            title: label("review", { capitalize: true }),
            subtitle: "Self/manager, upward, and peer",
            icon: getTopicflowIcon("assessment"),
            iconColor: "text-amber-800",
            selected: complianceProgramType === ComplianceProgramType.REVIEW,
            disabled: !canUpdateTemplates,
            onClick: () =>
              onChangeComplianceProgramType(ComplianceProgramType.REVIEW),
          },
          {
            title: "Engagement Survey",
            subtitle: "Employee engagement",
            icon: getTopicflowIcon("survey"),
            iconColor: "text-green-600",
            selected:
              complianceProgramType === ComplianceProgramType.ENGAGEMENT_SURVEY,
            disabled: !canUpdateTemplates,
            onClick: () =>
              onChangeComplianceProgramType(
                ComplianceProgramType.ENGAGEMENT_SURVEY
              ),
          },
        ]}
      />

      <div className="mt-12 tf-header-base-dark">
        {complianceProgramType === ComplianceProgramType.ENGAGEMENT_SURVEY
          ? "Survey Kick-off"
          : `${label("review", {
              capitalize: true,
            })} Kick-off`}
      </div>
      {complianceProgramType === ComplianceProgramType.REVIEW && (
        <div className="mt-1 text-gray-500">
          All items within a step will be available at the same time. Each step
          will be available once the previous step is completed.
        </div>
      )}

      <div className="mt-3 border rounded-md p-6">
        <FormFields
          fields={compact([
            {
              title:
                complianceProgramType ===
                ComplianceProgramType.ENGAGEMENT_SURVEY
                  ? "Survey name"
                  : `${label("review", { capitalize: true })} name`,
              render: () => (
                <Input
                  aria-label="Compliance program title input"
                  value={proposedComplianceProgram.title ?? ""}
                  placeholder={`Enter a ${label("review")} name...`}
                  onChange={(e) =>
                    onUpdateProposedComplianceProgram({
                      ...proposedComplianceProgram,
                      title: e.target.value,
                    })
                  }
                />
              ),
            },
            {
              title: "Cycle type",
              tooltip: `This will determine what conditions will make your program start.\n\nSingle cycle: This is a “one-off” program that will only run once and start and end on specific dates (with the option to have it repeat).\n\nConditional: Use this option for continuous programs (e.g. 90-day probationary ${label(
                "review"
              )}). New employees get added to the program based on their employee start date.`,
              render: () => (
                <div className="w-fit">
                  <ToggleButtonGroup<ProgramStartTrigger>
                    theme={ToggleButtonGroupTheme.defaultButtons}
                    buttons={[
                      {
                        title: "Single Cycle",
                        active: !proposedComplianceProgram.ongoing,
                        value: ProgramStartTrigger.ON_START_DATE,
                      },
                      {
                        title: "Conditional",
                        active: !!proposedComplianceProgram.ongoing,
                        value: ProgramStartTrigger.EMPLOYEE_START_DATE,
                      },
                    ]}
                    onClick={(buttonGroup) => {
                      const isOngoing =
                        buttonGroup.value ===
                        ProgramStartTrigger.EMPLOYEE_START_DATE;
                      onUpdateProposedComplianceProgram({
                        ...proposedComplianceProgram,
                        startDate: null,
                        startDateOffset: null,
                        dueDate: null,
                        startDateDueDateOffset: null,
                        ongoing: isOngoing,
                        recurrence: isOngoing
                          ? ComplianceProgramRecurrence.None
                          : proposedComplianceProgram.recurrence,
                      });
                    }}
                  />
                </div>
              ),
            },
            {
              title: "Start and due date",
              render: () =>
                proposedComplianceProgram.ongoing ? (
                  <div className="flex gap-2 items-center justify-between">
                    <div className="text-sm">Starts</div>
                    <Input
                      className="w-24"
                      type="number"
                      min={0}
                      step={1}
                      aria-label="Compliance program start day offset"
                      value={proposedComplianceProgram.startDateOffset ?? ""}
                      onChange={(e) =>
                        onUpdateProposedComplianceProgram({
                          ...proposedComplianceProgram,
                          startDateOffset: Math.trunc(Number(e.target.value)),
                        })
                      }
                    />
                    <div className="text-sm">days after start date</div>
                    <TopicflowIcon
                      type="rightArrow"
                      className="text-gray-500 w-4 h-4 shrink-0"
                    />
                    <div className="text-sm">Ends</div>
                    <Input
                      className="w-24"
                      type="number"
                      min={0}
                      step={1}
                      aria-label="Compliance program due date offset"
                      value={
                        proposedComplianceProgram.startDateDueDateOffset ?? ""
                      }
                      onChange={(e) =>
                        onUpdateProposedComplianceProgram({
                          ...proposedComplianceProgram,
                          startDateDueDateOffset: Math.trunc(
                            Number(e.target.value)
                          ),
                        })
                      }
                    />
                    <div className="text-sm">days after start date</div>
                  </div>
                ) : (
                  <div className="flex gap-2 items-center">
                    <InputDatePicker
                      date={proposedComplianceProgram.startDate}
                      onChangeDate={(date) =>
                        onUpdateProposedComplianceProgram({
                          ...proposedComplianceProgram,
                          startDate: date
                            ? moment(date).format("YYYY-MM-DD")
                            : null,
                        })
                      }
                      wrapperClassName="flex-1"
                      placeholderText="Start date"
                      ariaLabelledBy="Compliance program start date"
                    />
                    <TopicflowIcon
                      type="rightArrow"
                      className="text-gray-500 w-4 h-4"
                    />
                    <InputDatePicker
                      date={proposedComplianceProgram.dueDate}
                      onChangeDate={(date) =>
                        onUpdateProposedComplianceProgram({
                          ...proposedComplianceProgram,
                          dueDate: date
                            ? moment(date).format("YYYY-MM-DD")
                            : null,
                        })
                      }
                      wrapperClassName="flex-1"
                      placeholderText="Due date"
                      ariaLabelledBy="Compliance program due date"
                    />
                  </div>
                ),
            },
            !proposedComplianceProgram.ongoing && {
              title: `Repeat this ${
                complianceProgramType ===
                ComplianceProgramType.ENGAGEMENT_SURVEY
                  ? "survey"
                  : label("review")
              }`,
              render: () => (
                <Select<ComplianceProgramRecurrence>
                  value={
                    proposedComplianceProgram.recurrence ??
                    ComplianceProgramRecurrence.None
                  }
                  options={[
                    {
                      label: "Never",
                      value: ComplianceProgramRecurrence.None,
                    },
                    {
                      label: "Monthly",
                      value: ComplianceProgramRecurrence.Monthly,
                    },
                    {
                      label: "Quarterly",
                      value: ComplianceProgramRecurrence.Quarterly,
                    },
                    {
                      label: "Yearly",
                      value: ComplianceProgramRecurrence.Yearly,
                    },
                  ]}
                  onChange={(opt) =>
                    onUpdateProposedComplianceProgram({
                      ...proposedComplianceProgram,
                      recurrence: opt.value,
                    })
                  }
                />
              ),
            },
            complianceProgramType !==
              ComplianceProgramType.ENGAGEMENT_SURVEY && {
              title: "Evaluation period",
              tooltip:
                "This period of time will be used as a reference for the system to show relevant goals, work history, role details, etc.",
              render: () =>
                proposedComplianceProgram.ongoing ? (
                  <div className="flex gap-2 items-center justify-between">
                    <div className="text-sm">Starts</div>
                    <Input
                      type="number"
                      className="w-24"
                      min={0}
                      step={1}
                      aria-label="Compliance program due date offset"
                      value={
                        proposedComplianceProgram.startDatePeriodStartOffset ??
                        ""
                      }
                      onChange={(e) =>
                        onUpdateProposedComplianceProgram({
                          ...proposedComplianceProgram,
                          startDatePeriodStartOffset: Math.trunc(
                            Number(e.target.value)
                          ),
                        })
                      }
                    />
                    <div className="text-sm">days after start date</div>
                    <TopicflowIcon
                      type="rightArrow"
                      className="text-gray-500 w-4 h-4 shrink-0"
                    />
                    <div className="text-sm">Ends</div>
                    <Input
                      type="number"
                      className="w-24"
                      min={0}
                      step={1}
                      aria-label="Compliance program due date offset"
                      value={
                        proposedComplianceProgram.startDatePeriodEndOffset ?? ""
                      }
                      onChange={(e) =>
                        onUpdateProposedComplianceProgram({
                          ...proposedComplianceProgram,
                          startDatePeriodEndOffset: Math.trunc(
                            Number(e.target.value)
                          ),
                        })
                      }
                    />
                    <div className="text-sm">days after start date</div>
                  </div>
                ) : (
                  <>
                    <Select<ProgramEvaluationPeriodType>
                      value={proposedComplianceProgram.evaluationPeriod.type}
                      options={[
                        {
                          label: "Past week",
                          value: ProgramEvaluationPeriodType.PAST_WEEK,
                        },
                        {
                          label: "Past 2 weeks",
                          value: ProgramEvaluationPeriodType.PAST_TWO_WEEKS,
                        },
                        {
                          label: "Past month",
                          value: ProgramEvaluationPeriodType.PAST_MONTH,
                        },
                        {
                          label: "Past 2 months",
                          value: ProgramEvaluationPeriodType.PAST_TWO_MONTHS,
                        },
                        {
                          label: "Past quarter",
                          value: ProgramEvaluationPeriodType.PAST_QUARTER,
                        },
                        {
                          label: "Past 6 months",
                          value: ProgramEvaluationPeriodType.PAST_HALF_YEAR,
                        },
                        {
                          label: "Past year",
                          value: ProgramEvaluationPeriodType.PAST_YEAR,
                        },
                        {
                          label: "Custom date range",
                          value: ProgramEvaluationPeriodType.CUSTOM,
                        },
                      ]}
                      onChange={(opt) =>
                        onUpdateProposedComplianceProgram({
                          ...proposedComplianceProgram,
                          evaluationPeriod: {
                            type: opt.value,
                            startDate:
                              opt.value !== ProgramEvaluationPeriodType.CUSTOM
                                ? undefined
                                : proposedComplianceProgram.evaluationPeriod
                                    .startDate,
                            endDate:
                              opt.value !== ProgramEvaluationPeriodType.CUSTOM
                                ? undefined
                                : proposedComplianceProgram.evaluationPeriod
                                    .endDate,
                          },
                        })
                      }
                    />
                    {proposedComplianceProgram.evaluationPeriod.type ===
                      ProgramEvaluationPeriodType.CUSTOM && (
                      <div className="mt-2 flex gap-2 items-center">
                        <InputDatePicker
                          date={
                            proposedComplianceProgram.evaluationPeriod.startDate
                          }
                          placeholderText="Start date"
                          wrapperClassName="flex-1"
                          onChangeDate={(date) =>
                            onUpdateProposedComplianceProgram({
                              ...proposedComplianceProgram,
                              evaluationPeriod: {
                                ...proposedComplianceProgram.evaluationPeriod,
                                startDate: date
                                  ? moment(date).format("YYYY-MM-DD")
                                  : undefined,
                              },
                            })
                          }
                          ariaLabelledBy="Compliance program period start date"
                        />
                        <TopicflowIcon
                          type="rightArrow"
                          className="text-gray-500 w-4 h-4"
                        />
                        <InputDatePicker
                          date={
                            proposedComplianceProgram.evaluationPeriod.endDate
                          }
                          placeholderText="End date"
                          wrapperClassName="flex-1"
                          onChangeDate={(date) =>
                            onUpdateProposedComplianceProgram({
                              ...proposedComplianceProgram,
                              evaluationPeriod: {
                                ...proposedComplianceProgram.evaluationPeriod,
                                endDate: date
                                  ? moment(date).format("YYYY-MM-DD")
                                  : undefined,
                              },
                            })
                          }
                          ariaLabelledBy="Compliance program period end date"
                        />
                      </div>
                    )}
                  </>
                ),
            },
            complianceProgramType ===
              ComplianceProgramType.ENGAGEMENT_SURVEY && {
              title: "Anonymity",
              render: () =>
                selectedEngagementSurveyAssessmentTemplate && (
                  <Select<AssessmentGroupAnonymity>
                    disabled={!canUpdate}
                    value={assertNonNull(
                      selectedEngagementSurveyAssessmentTemplate.anonymity
                    )}
                    options={assessmentGroupAnonymityOptions(
                      AssessmentType.EngagementSurvey
                    )}
                    onChange={(opt) =>
                      onUpdateAssessmentTemplateSettings(
                        AssessmentType.EngagementSurvey
                      )({
                        anonymity: opt.value,
                      })
                    }
                  />
                ),
            },
          ])}
        />
      </div>

      {complianceProgramType !== ComplianceProgramType.ENGAGEMENT_SURVEY && (
        <>
          <div className="mt-12 tf-header-base-dark">{`Step 1: Employee ${label(
            "review",
            {
              capitalize: true,
              pluralize: true,
            }
          )}`}</div>
          <div className="mt-3 flex flex-col gap-3">
            <Step
              title={`Pre-${label("review", { capitalize: true })} Calibration`}
              enabled={
                proposedComplianceProgram.calibration ===
                ComplianceProgramCalibrationType.PreCalibration
              }
              disabled={
                !complianceProgramSteps.includes(
                  ComplianceProgramStep.SELF_MANAGER_REVIEW
                ) || !canUpdate
              }
              infoTooltip={`You may only use one of the calibration types (pre/post-${label(
                "review"
              )}) in your ${label("review")}.`}
              toggleTooltip={
                complianceProgramSteps.includes(
                  ComplianceProgramStep.SELF_MANAGER_REVIEW
                )
                  ? undefined
                  : `Pre-${label("review", {
                      capitalize: true,
                    })} Calibration requires Downward ${label("review", {
                      capitalize: true,
                    })}`
              }
              onChange={(enabled) =>
                onUpdateProposedComplianceProgram((prev) => ({
                  ...prev,
                  calibration: enabled
                    ? ComplianceProgramCalibrationType.PreCalibration
                    : ComplianceProgramCalibrationType.Off,
                }))
              }
            />
            <Step
              title={`Self ${label("review", { capitalize: true })}`}
              enabled={complianceProgramSteps.includes(
                ComplianceProgramStep.SELF_MANAGER_REVIEW
              )}
              infoTooltip={`The self ${label("review")} and downward ${label(
                "review"
              )} are currently linked. You cannot enable one without enabling the other.`}
              disabled={!canUpdate}
              onChange={() =>
                handleToggleStep(ComplianceProgramStep.SELF_MANAGER_REVIEW)
              }
            />
            <Step
              title={`Peer ${label("review", { capitalize: true })}`}
              enabled={complianceProgramSteps.includes(
                ComplianceProgramStep.PEER_REVIEW
              )}
              disabled={!canUpdate}
              settings={
                complianceProgramSteps.includes(
                  ComplianceProgramStep.PEER_REVIEW
                ) && (
                  <FormFields
                    fields={[
                      {
                        title: "Providers",
                        render: () =>
                          selectedPeerAssessmentTemplate ? (
                            <DeprecatedSelect<AssessmentGroupProviders>
                              disabled={!canUpdate}
                              value={assertNonNull(
                                selectedPeerAssessmentTemplate.providers
                              )}
                              options={assessmentGroupProviderOptions(
                                AssessmentType.Peer
                              )}
                              onChange={(opt) =>
                                onUpdateAssessmentTemplateSettings(
                                  AssessmentType.Peer
                                )({
                                  providers: opt.value,
                                })
                              }
                              aria-label="Assessment providers select"
                            />
                          ) : (
                            <div className="text-sm h-10 flex items-center">
                              -
                            </div>
                          ),
                      },
                      {
                        title: "Anonymity",
                        render: () =>
                          selectedPeerAssessmentTemplate ? (
                            <DeprecatedSelect<AssessmentGroupAnonymity>
                              disabled={!canUpdate}
                              value={assertNonNull(
                                selectedPeerAssessmentTemplate.anonymity
                              )}
                              options={assessmentGroupAnonymityOptions(
                                AssessmentType.Peer
                              )}
                              onChange={(opt) =>
                                onUpdateAssessmentTemplateSettings(
                                  AssessmentType.Peer
                                )({
                                  anonymity: opt.value,
                                })
                              }
                            />
                          ) : (
                            <div className="text-sm h-10 flex items-center">
                              -
                            </div>
                          ),
                      },
                    ]}
                  />
                )
              }
              onChange={() =>
                handleToggleStep(ComplianceProgramStep.PEER_REVIEW)
              }
            />
            <Step
              title={`Upward ${label("review", { capitalize: true })}`}
              enabled={complianceProgramSteps.includes(
                ComplianceProgramStep.UPWARD_REVIEW
              )}
              disabled={!canUpdate}
              settings={
                complianceProgramSteps.includes(
                  ComplianceProgramStep.UPWARD_REVIEW
                ) && (
                  <FormFields
                    fields={[
                      {
                        title: "Anonymity",
                        render: () =>
                          selectedManagerAssessmentTemplate ? (
                            <DeprecatedSelect<AssessmentGroupAnonymity>
                              disabled={!canUpdate}
                              value={assertNonNull(
                                selectedManagerAssessmentTemplate.anonymity
                              )}
                              options={assessmentGroupAnonymityOptions(
                                AssessmentType.Manager
                              )}
                              onChange={(opt) =>
                                onUpdateAssessmentTemplateSettings(
                                  AssessmentType.Manager
                                )({
                                  anonymity: opt.value,
                                })
                              }
                            />
                          ) : (
                            <div className="text-sm h-10 flex items-center">
                              -
                            </div>
                          ),
                      },
                    ]}
                  />
                )
              }
              onChange={() =>
                handleToggleStep(ComplianceProgramStep.UPWARD_REVIEW)
              }
            />
          </div>

          <div className="mt-12 tf-header-base-dark">{`Step 2: Manager ${label(
            "review",
            {
              capitalize: true,
            }
          )}`}</div>
          <div className="mt-3 flex flex-col gap-3">
            <Step
              title={`Downward ${label("review", { capitalize: true })}`}
              enabled={complianceProgramSteps.includes(
                ComplianceProgramStep.SELF_MANAGER_REVIEW
              )}
              infoTooltip={`The self ${label("review")} and downward ${label(
                "review"
              )} are currently linked. You cannot enable one without enabling the other.`}
              disabled={!canUpdate}
              onChange={() =>
                handleToggleStep(ComplianceProgramStep.SELF_MANAGER_REVIEW)
              }
            />
          </div>

          <div className="mt-12 tf-header-base-dark">{`Step 3: Post-${label(
            "review",
            {
              capitalize: true,
            }
          )}`}</div>
          <div className="mt-3 flex flex-col gap-3">
            <Step
              title={`Post-${label("review", {
                capitalize: true,
              })} Calibration`}
              disabled={
                !complianceProgramSteps.includes(
                  ComplianceProgramStep.SELF_MANAGER_REVIEW
                ) || !canUpdate
              }
              infoTooltip={`You may only use one of the calibration types (pre/post-${label(
                "review"
              )}) in your ${label("review")}.`}
              toggleTooltip={
                complianceProgramSteps.includes(
                  ComplianceProgramStep.SELF_MANAGER_REVIEW
                )
                  ? undefined
                  : `Post-${label("review", {
                      capitalize: true,
                    })} Calibration requires Downward ${label("review", {
                      capitalize: true,
                    })}`
              }
              enabled={
                proposedComplianceProgram.calibration ===
                ComplianceProgramCalibrationType.PostCalibration
              }
              onChange={(enabled) => {
                onUpdateProposedComplianceProgram((prev) => ({
                  ...prev,
                  calibration: enabled
                    ? ComplianceProgramCalibrationType.PostCalibration
                    : ComplianceProgramCalibrationType.Off,
                }));
              }}
            />
            <Step
              title={`Share ${label("review", {
                capitalize: true,
              })} with Subject`}
              enabled={proposedComplianceProgram.sharesWithSubject}
              disabled={selectedAssessmentTemplates.length === 0 || !canUpdate}
              infoTooltip={`This determines whether the final ${label(
                "review"
              )} will be shared with the subject.`}
              toggleTooltip={
                selectedAssessmentTemplates.length === 0
                  ? `Sharing requires a ${label("review", {
                      capitalize: true,
                    })}`
                  : undefined
              }
              onChange={(enabled) => {
                onUpdateProposedComplianceProgram((prev) => ({
                  ...prev,
                  sharesWithSubject: enabled,
                }));
              }}
            />
            <Step
              title="1:1 Meeting"
              enabled={complianceProgramSteps.includes(
                ComplianceProgramStep.ONE_ON_ONE
              )}
              disabled={!canUpdate}
              settings={
                complianceProgramSteps.includes(
                  ComplianceProgramStep.ONE_ON_ONE
                ) && (
                  <FormFields
                    fields={[
                      {
                        title: "Meeting template",
                        render: () => (
                          <ComboboxGeneric
                            options={availableTopicTemplates}
                            disabled={!canUpdate}
                            onChangeValue={(opt) =>
                              onUpdateProposedComplianceProgram((prev) => ({
                                ...prev,
                                topicTemplates: [{ id: opt.value }],
                              }))
                            }
                            value={
                              selectedTopicTemplateId.length
                                ? availableTopicTemplates.find(
                                    ({ value }) =>
                                      value ===
                                      assertNonNull(selectedTopicTemplateId[0])
                                        .id
                                  ) ?? null
                                : null
                            }
                          />
                        ),
                      },
                    ]}
                  />
                )
              }
              onChange={() =>
                handleToggleStep(ComplianceProgramStep.ONE_ON_ONE)
              }
            />
          </div>
        </>
      )}
    </>
  );
};

export default ProgramFormWorkflow;
